// Commandes tactiles en jeu : JOYSTICK rond + creuser G/D + objet + pause + son.
// Le joystick est une grande zone du coin bas-gauche dont la base vient se poser SOUS LE POUCE
// (refonte du 31/07 ; avant, une croix à positions fixes qu'il fallait viser). On glisse d'une
// direction à l'autre sans lever — courir puis grimper une échelle d'un même mouvement.
// Les boutons d'action restent des boutons indépendants multi-touch (pointerdown/up/cancel + capture).
// Libellés statiques uniquement (aucun texte utilisateur : textContent sans risque).
import { ItemKind, NO_INPUT, PlayerInput, PlayerInputBoolKey, UseDir } from '../shared/types'
import { accepteNouveauPointeur, axeDominant, axeSecondaire, SEUIL_SECONDAIRE } from './gestures'

export interface TouchHandlers { onPause(): void; onMute(): void; onRestart?(): void }
export interface TouchControls {
  current(): PlayerInput
  attach(): void
  detach(): void
  setMuted(m: boolean): void
  /** Icône du bouton OBJET = objet équipé (16/07) : 💣 ⛏ ⚡ ➰, ● grisé si les mains sont vides. */
  setItem(item: ItemKind | null): void
  /** OUTIL EN MAIN (06/08, demande Claude-Alain : « les touches sur le smartphone doivent
   *  changer le visuel de pioche en maillet »). Bascule les deux boutons d'action gauche/droite
   *  entre la pioche et le maillet, avec un éclat au changement. Appelé à chaque tick. */
  setOutil(maillet: boolean): void
  /** DANS L'EAU (06/08) : le joystick rend la DIAGONALE, que le reste du jeu n'a pas. Appelé à
   *  chaque tick avec l'immersion réelle du coureur (sa tuile, pas son souffle). */
  setNage(immerge: boolean): void
}

type Dir = 'up' | 'down' | 'left' | 'right'
// Les quatre directions du jeu. Depuis le passage au joystick (31/07) elles n'ont plus ni
// libellé ni classe propre : le repère visuel est une pastille d'axe, pas une flèche-bouton.
const DIRS: { key: Dir }[] = [{ key: 'up' }, { key: 'down' }, { key: 'left' }, { key: 'right' }]
interface Def { cls: string; label: string; key?: PlayerInputBoolKey; tap?: 'pause' | 'mute' | 'restart' }
const ACTIONS: Def[] = [
  { cls: 'tc-digl', label: '⛏◀', key: 'digL' }, { cls: 'tc-digr', label: '▶⛏', key: 'digR' },
  { cls: 'tc-use', label: '●', key: 'use' },
  { cls: 'tc-dash', label: '»', key: 'dash' }, // Lot D : bouton dash (au-dessus de l'objet)
  { cls: 'tc-pause', label: '⏸', tap: 'pause' }, { cls: 'tc-restart', label: '🔄', tap: 'restart' }, { cls: 'tc-mute', label: '🔊', tap: 'mute' },
]

export function createTouchControls(handlers: TouchHandlers): TouchControls {
  const root = document.getElementById('touch')
  if (!root) throw new Error('Commandes tactiles : élément #touch introuvable dans index.html')
  const state: PlayerInput = { ...NO_INPUT }
  let muteBtn: HTMLButtonElement | null = null
  let useBtn: HTMLButtonElement | null = null // bouton OBJET : icône dynamique (16/07)
  // OUTIL EN MAIN (06/08) : on garde les deux boutons d'action sous la main pour basculer leur
  // icône pioche <-> maillet et rejouer l'éclat de l'échange.
  let digLBtn: HTMLButtonElement | null = null, digRBtn: HTMLButtonElement | null = null
  let outilMaillet = false
  // NAGE (06/08) : dans l'eau, le stick transmet AUSSI l'axe secondaire. `rappliquerStick` rejoue
  // la dernière position du pouce — indispensable car on entre le plus souvent dans l'eau en
  // TOMBANT, pouce immobile : sans ça, la diagonale n'apparaîtrait qu'au prochain geste.
  let immerge = false
  let rappliquerStick: (() => void) | null = null
  let useIcon = '●'                            // icône courante (restaurée après le retour visuel du glissé)
  // Pulse d'usage (#5) : le bouton objet pose ici { dir } à la relâche ; current() le renvoie UNE
  // fois (use=true + useDir) puis le vide -> le tick suivant lit use=false, donc useHeld se réarme
  // et deux appuis rapprochés déclenchent bien DEUX actions (poser plusieurs cordes vite).
  let usePulse: { dir: UseDir } | null = null

  /* ══════════════════════ LE JOYSTICK ROND (31/07) ══════════════════════
   * Demandé par Claude-Alain : « je veux un joystick rond qui permet de glisser dans toutes les
   * directions ». L'ancienne croix imposait de VISER — quatre pastilles à des positions fixes, et
   * un pouce posé à côté ne déclenchait rien.
   *
   * Ici la base est FLOTTANTE : elle vient se poser là où le pouce touche, n'importe où dans une
   * grande zone du coin bas-gauche. On ne cherche plus le pad du regard, ce qui est tout l'intérêt
   * sur un téléphone qu'on tient à deux mains sans regarder ses pouces.
   *
   * ⚠️ LE JEU N'A PAS DE DIAGONALE (`moveRunner` traite le vertical et l'horizontal séparément).
   * Le joystick lit donc TOUS les angles mais tranche sur l'axe dominant. Sans repère visuel on
   * croirait à un bug en poussant en biais : les quatre pastilles d'axe montrent ce qui est retenu.
   */
  const RAYON = 46      // course maximale du pommeau, en px (la base fait 132 de diamètre)
  const MORTE = 13      // zone morte centrale : sous ce rayon, aucune direction
  function makeStick(): HTMLDivElement {
    const zone = document.createElement('div')
    zone.className = 'tc-stick'
    const base = document.createElement('div')
    base.className = 'tc-stick-base'
    const knob = document.createElement('div')
    knob.className = 'tc-stick-knob'
    const axes = new Map<Dir, HTMLDivElement>()
    for (const d of DIRS) {
      const a = document.createElement('div')
      a.className = 'tc-stick-axe tc-axe-' + d.key
      axes.set(d.key, a)
      base.appendChild(a)
    }
    base.appendChild(knob)
    zone.appendChild(base)

    let pointerId = -1   // le pouce qui tient le stick (-1 : aucun) ; les autres doigts l'ignorent
    let cx = 0, cy = 0   // centre de la base, en coordonnées de la ZONE
    let tenu: Dir | null = null   // l'axe retenu — mémorisé, car l'hystérésis en dépend
    let dernierDx = 0, dernierDy = 0 // dernière position du pommeau, à rejouer si l'immersion change

    /** Position de repos : en bas à gauche de la zone, là où le pouce tombe naturellement. */
    function repos(): void {
      const r = zone.getBoundingClientRect()
      poserBase(Math.min(100, r.width / 2), Math.max(r.height - 104, r.height / 2))
      tenu = null
      appliquer(0, 0)
    }
    /** Déplace la BASE. Elle reste ENTIÈRE dans la zone : posée au ras du bord, la moitié du
     *  joystick sortirait de l'écran et la moitié des directions deviendrait inatteignable. */
    function poserBase(x: number, y: number): void {
      const r = zone.getBoundingClientRect()
      cx = Math.max(82, Math.min(r.width - 82, x))
      cy = Math.max(82, Math.min(r.height - 82, y))
      base.style.transform = `translate(${cx - 66}px, ${cy - 66}px)`   // 66 = demi-base
    }
    /** Pose le pommeau et allume le ou les axes retenus. `dx, dy` sont DÉJÀ bornés au rayon. */
    function appliquer(dx: number, dy: number): void {
      dernierDx = dx; dernierDy = dy
      base.style.transform = `translate(${cx - 66}px, ${cy - 66}px)`
      knob.style.transform = `translate(${35 + dx}px, ${35 + dy}px)`   // 66 - 31 = 35
      // EN NAGEANT SEULEMENT : la seconde composante du geste compte aussi (la branche eau de
      // `moveRunner` lit les deux axes du même tick). Hors de l'eau, `sec` reste null et on
      // retrouve exactement le comportement mono-axe d'avant, hystérésis comprise.
      const sec = immerge ? axeSecondaire(dx, dy, tenu, SEUIL_SECONDAIRE * RAYON) : null
      for (const de of DIRS) {
        const on = de.key === tenu || de.key === sec
        if (state[de.key] !== on) state[de.key] = on
        axes.get(de.key)!.classList.toggle('is-on', on)
      }
    }
    /**
     * Le pouce a bougé : `tx, ty` en coordonnées de la ZONE.
     *
     * ⚠️ LA BASE SUIT LE POUCE au-delà du rayon (31/07). Figée, on finissait « à court de course »
     * sur une longue traversée : le pouce dérivait, et surtout son ANGLE changeait, ce qui pouvait
     * faire basculer l'axe tout seul. Elle glisse donc pour que le pommeau reste au bord.
     * Si elle bute sur le bord de la zone, c'est le pommeau qui se borne, comme avant.
     */
    function suivre(tx: number, ty: number): void {
      let dx = tx - cx, dy = ty - cy
      const d = Math.hypot(dx, dy)
      if (d > RAYON) {
        poserBase(cx + dx * (1 - RAYON / d), cy + dy * (1 - RAYON / d))
        dx = tx - cx; dy = ty - cy                  // cx/cy ont pu être bornés : on recalcule
        const d2 = Math.hypot(dx, dy)
        if (d2 > RAYON) { dx *= RAYON / d2; dy *= RAYON / d2 }
      }
      tenu = axeDominant(dx, dy, tenu, MORTE)
      appliquer(dx, dy)
    }

    zone.addEventListener('pointerdown', (e) => {
      // ⚠️ ON REPREND LA MAIN SI LE POINTEUR PRÉCÉDENT N'EXISTE PLUS (04/08). Écrit `if (pointerId
      // !== -1) return`, ce garde condamnait le joystick dès qu'une relâche se perdait : la
      // direction restait vraie ET tout nouveau doigt était rejeté. MESURÉ : page mise en
      // arrière-plan avec le pouce posé -> « left » actif pour toujours, plus aucune reprise
      // possible, il fallait quitter le jeu en forçant. Un SECOND doigt posé pendant qu'on joue,
      // lui, ne vole toujours rien : sa capture est vive.
      if (!accepteNouveauPointeur(pointerId, pointerId !== -1 && zone.hasPointerCapture(pointerId))) return
      e.preventDefault()
      pointerId = e.pointerId
      zone.setPointerCapture(e.pointerId)
      zone.classList.add('is-held')
      const r = zone.getBoundingClientRect()
      tenu = null
      poserBase(e.clientX - r.left, e.clientY - r.top)  // LA BASE VIENT AU POUCE, pas l'inverse
      appliquer(0, 0)
    })
    zone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== pointerId) return
      const r = zone.getBoundingClientRect()
      suivre(e.clientX - r.left, e.clientY - r.top)
    })
    /** Rend le stick : plus personne ne le tient, plus aucune direction. LE seul endroit qui
     *  libère `pointerId` — quatre chemins y mènent (relâche, annulation, capture perdue, page
     *  quittée), et il est idempotent puisque deux d'entre eux peuvent arriver ensemble. */
    function relache(): void {
      pointerId = -1
      zone.classList.remove('is-held')
      repos()
    }
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== pointerId) return
      relache()
    }
    zone.addEventListener('pointerup', end)
    zone.addEventListener('pointercancel', end)
    // `lostpointercapture` : le navigateur nous a repris le doigt (geste système, élément retiré
    // du DOM). C'est le seul événement qui arrive à coup sûr dans ce cas — `pointerup`, lui, peut
    // ne jamais venir.
    zone.addEventListener('lostpointercapture', (e) => { if (e.pointerId === pointerId) relache() })
    zone.addEventListener('contextmenu', (e) => e.preventDefault())
    // ⚠️ LES DEUX FILETS QUI CORRIGENT LE BUG MESURÉ : une page qu'on quitte des yeux — notification,
    // bascule d'application, écran verrouillé — ne recevra JAMAIS la relâche du doigt posé. Sans
    // eux, le personnage repartait en courant tout seul au retour, dans la dernière direction tenue.
    const lacher = (): void => { if (pointerId !== -1) relache() }
    addEventListener('blur', lacher)
    document.addEventListener('visibilitychange', () => { if (document.hidden) lacher() })
    // La zone est dimensionnée en vw/vh : sa taille change à la rotation, le repos doit suivre.
    addEventListener('resize', () => { if (pointerId === -1) repos() })
    rappliquerStick = () => appliquer(dernierDx, dernierDy) // relire le geste en cours quand on entre/sort de l'eau
    queueMicrotask(repos)                           // après insertion dans le DOM (getBoundingClientRect)
    return zone
  }

  function makeBtn(def: Def): HTMLButtonElement {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = 'tc-btn ' + def.cls
    b.textContent = def.label
    if (def.key === 'digL') digLBtn = b
    if (def.key === 'digR') digRBtn = b
    if (def.key === 'use') {
      // Bouton OBJET directionnel (#5) : un TAP utilise l'objet dans la FACE ; un SWIPE (4 sens
      // depuis 16/07 soir) l'utilise dans ce sens. On déclenche à la RELÂCHE avec le sens du glissé, via `useDir`
      // (n'affecte JAMAIS le mouvement). useHeld côté moteur garantit un seul usage par pression.
      useBtn = b
      b.textContent = useIcon // icône dynamique (16/07) : setItem la tient à jour à chaque tick
      const THRESH = 16 // px de glissé minimal pour qu'un swipe compte
      let sx = 0, sy = 0, dir: UseDir = null, pressed = false
      // Retour visuel du glissé (16/07) : pendant le drag, le bouton montre la FLÈCHE du sens
      // retenu (l'icône revient à la relâche via setItem/restore).
      const showDir = (): void => { b.textContent = dir === 'left' ? '⬅' : dir === 'right' ? '➡' : dir === 'up' ? '⬆' : dir === 'down' ? '⬇' : useIcon }
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault(); b.setPointerCapture(e.pointerId)
        sx = e.clientX; sy = e.clientY; dir = null; pressed = true; b.classList.add('is-down')
      })
      b.addEventListener('pointermove', (e) => {
        if (!pressed) return
        const dx = e.clientX - sx, dy = e.clientY - sy
        dir = Math.abs(dx) < THRESH && Math.abs(dy) < THRESH ? null
          : Math.abs(dy) > Math.abs(dx) ? (dy < 0 ? 'up' : 'down') // bas explicite (16/07 soir : marteau tous sens)
            : (dx < 0 ? 'left' : 'right')
        showDir()
      })
      const fire = () => {
        if (!pressed) return
        pressed = false; b.classList.remove('is-down'); b.textContent = useIcon
        usePulse = { dir } // consommé UNE fois par current() -> un seul usage, réarmement propre entre 2 appuis
      }
      b.addEventListener('pointerup', fire)
      b.addEventListener('pointercancel', () => { pressed = false; b.classList.remove('is-down'); b.textContent = useIcon })
      b.addEventListener('contextmenu', (e) => e.preventDefault())
    } else if (def.key) {
      const k = def.key
      const press = (e: PointerEvent) => {
        e.preventDefault()
        b.setPointerCapture(e.pointerId)
        state[k] = true
        b.classList.add('is-down')
      }
      const release = () => { state[k] = false; b.classList.remove('is-down') }
      b.addEventListener('pointerdown', press)
      b.addEventListener('pointerup', release)
      b.addEventListener('pointercancel', release)
      // contextmenu (appui long iOS/Android) : ni menu ni blocage du bouton
      b.addEventListener('contextmenu', (e) => e.preventDefault())
    } else {
      b.addEventListener('click', (e) => {
        e.preventDefault()
        if (def.tap === 'pause') handlers.onPause()
        else if (def.tap === 'restart') handlers.onRestart?.()
        else handlers.onMute()
      })
      b.addEventListener('contextmenu', (e) => e.preventDefault())
      if (def.tap === 'mute') muteBtn = b
    }
    return b
  }

  return {
    current: () => {
      const out = { ...state }
      if (usePulse) { out.use = true; out.useDir = usePulse.dir; usePulse = null } // pulse d'usage (#5), consommé une fois
      return out
    },
    attach() {
      // Le bouton « recommencer » n'existe qu'en solo (l'appelant fournit onRestart) : la Mine et le
      // versus ne le montrent pas (recommencer n'y a pas de sens).
      root.replaceChildren(makeStick(), ...ACTIONS.filter((d) => d.tap !== 'restart' || handlers.onRestart).map(makeBtn))
      root.hidden = false
    },
    detach() {
      root.hidden = true
      root.replaceChildren()
      Object.assign(state, NO_INPUT); usePulse = null // remet tout à false + useDir null + pulse vidé
      useBtn = null; useIcon = '●'
      digLBtn = null; digRBtn = null; outilMaillet = false
      immerge = false; rappliquerStick = null // le stick est détruit : sa fonction de rappel avec
    },
    setMuted(m: boolean) { if (muteBtn) muteBtn.textContent = m ? '🔇' : '🔊' },
    // NAGE (06/08) : bascule la diagonale. Ne fait rien tant que l'immersion ne CHANGE pas (appelé
    // à chaque tick) ; au changement, on rejoue la position du pouce pour que la seconde direction
    // s'allume — ou s'éteigne — immédiatement, sans attendre qu'il bouge.
    setNage(dansLEau: boolean) {
      if (dansLEau === immerge) return
      immerge = dansLEau
      rappliquerStick?.()
    },
    // OUTIL EN MAIN (06/08) : les deux boutons d'action changent d'icône ET d'aura. La classe
    // `is-swap` déclenche l'éclat CSS ; on la retire au bout de l'animation pour qu'elle puisse
    // se rejouer au prochain échange (retirer/remettre une classe ne relance pas une animation
    // dans le même frame — d'où le double requestAnimationFrame).
    setOutil(maillet: boolean) {
      if (maillet === outilMaillet) return
      outilMaillet = maillet
      for (const [btn, gauche] of [[digLBtn, true], [digRBtn, false]] as [HTMLButtonElement | null, boolean][]) {
        if (!btn) continue
        btn.textContent = maillet ? (gauche ? '🔨◀' : '▶🔨') : (gauche ? '⛏◀' : '▶⛏')
        btn.classList.toggle('is-maillet', maillet)
        btn.classList.remove('is-swap')
        requestAnimationFrame(() => requestAnimationFrame(() => btn.classList.add('is-swap')))
      }
    },
    // Icône du bouton OBJET = objet équipé. Appelé à chaque tick par le contrôleur (solo/online) ;
    // ne touche le DOM qu'au CHANGEMENT (cache useIcon) pour rester gratuit.
    setItem(item: ItemKind | null) {
      const icon = item === 'bomb' ? '💣' : item === 'jackhammer' ? '⛏' : item === 'tazer' ? '⚡' : item === 'rope' ? '➰' : '●'
      if (icon === useIcon) return
      useIcon = icon
      if (useBtn) {
        useBtn.textContent = icon
        useBtn.classList.toggle('is-empty', item === null)
      }
    },
  }
}
